<template>
  <div
    class="flex w-full flex-col items-center gap-y-8"
    :class="!data.is_logged_in ? 'mb-20' : ''"
  >
    <div class="flex w-full">
      <div class="flex w-full justify-start rounded-lg bg-white p-6 shadow-md">
        <div class="flex w-full flex-col justify-between gap-11 lg:flex-row">
          <!-- Product Image Section -->
          <div class="flex flex-col lg:w-[440px]">
            <div
              class="relative aspect-square overflow-hidden rounded-lg bg-gray-100 shadow-md transition-all duration-200 hover:shadow-lg"
            >
              <img
                :src="`/uploads/${selectedImage}`"
                :key="selectedImage"
                :alt="__('Media image')"
                class="h-full w-full object-cover"
              />

              <template v-if="data.product.images.length > 1">
                <div
                  @click="changeImage('back')"
                  class="absolute left-2 top-1/2 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-black/50"
                >
                  <Icon
                    name="arrow_back"
                    type="filled"
                    class="p-2 text-sm text-white"
                  >
                  </Icon>
                </div>

                <div
                  @click="changeImage('next')"
                  class="absolute right-2 top-1/2 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-black/50"
                >
                  <Icon
                    name="arrow_forward"
                    type="filled"
                    class="p-2 text-sm text-white"
                  >
                  </Icon>
                </div>

                <div
                  class="absolute bottom-2 flex w-full flex-row items-center justify-center gap-x-2"
                >
                  <div class="flex flex-row gap-x-2 rounded-xl bg-black/50 p-2">
                    <div
                      v-for="image in data.product.images"
                      class="h-1.5 w-1.5 rounded-full"
                      :class="
                        selectedImage === image.path
                          ? 'bg-white'
                          : 'bg-white/30'
                      "
                    ></div>
                  </div>
                </div>
              </template>
            </div>

            <!-- Thumbnail Images -->
            <div
              v-show="isNotMobile && data.product.images.length > 1"
              class="scrollbar-thin mt-4 flex gap-2 overflow-x-auto pb-2"
              :class="data.product.images.length <= 5 ? 'justify-center' : ''"
            >
              <div
                v-for="(image, index) in data?.product?.images"
                :key="image.id"
                class="h-24 w-24 flex-shrink-0 cursor-pointer overflow-hidden rounded-md bg-gray-100 object-cover shadow-md transition-all duration-200 hover:shadow-lg"
                @click="makePrimary(image)"
              >
                <img
                  :src="`/uploads/${image.path}`"
                  :key="image.id"
                  :alt="__('Media image')"
                  class="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>

          <!-- Product Details Section -->
          <div
            class="scrollbar-thin flex flex-col space-y-4 overflow-y-auto pl-2 pr-4 lg:w-2/3"
          >
            <!-- Product Title -->
            <h1 class="text-3xl font-semibold text-gray-900">
              {{ data.product.name }}
            </h1>

            <!-- Rating and Stock Info -->
            <div class="space-y-6">
              <div class="flex items-center gap-4">
                <template v-if="data.product.reviews_data.total_reviews > 0">
                  <span class="text-sm text-gray-600"
                    >{{ data.product.reviews_data.average_rating }}/5</span
                  >
                </template>

                <div class="flex">
                  <template
                    v-if="data.product.reviews_data.total_reviews !== 0"
                  >
                    <Icon
                      v-for="_ in data.product.reviews_data.average_rating
                        ? Math.floor(data.product.reviews_data.average_rating)
                        : 0"
                      name="star"
                      type="filled"
                      class="text-primary"
                    ></Icon>

                    <Icon
                      v-for="i in 5 -
                      (data.product.reviews_data.average_rating
                        ? Math.floor(data.product.reviews_data.average_rating)
                        : 0)"
                      name="star"
                      type="filled"
                      class="text-gray-300"
                    ></Icon>
                  </template>

                  <template v-else>
                    <Icon
                      v-for="i in 5"
                      name="star"
                      type="filled"
                      class="text-gray-300"
                    ></Icon>
                  </template>
                </div>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="14"
                  viewBox="0 0 15 14"
                  class="fill-primary"
                >
                  <path
                    d="M0.400024 14V1.4C0.400024 1.015 0.537108 0.685417 0.811274 0.41125C1.08544 0.137083 1.41502 0 1.80002 0H13C13.385 0 13.7146 0.137083 13.9888 0.41125C14.2629 0.685417 14.4 1.015 14.4 1.4V9.8C14.4 10.185 14.2629 10.5146 13.9888 10.7887C13.7146 11.0629 13.385 11.2 13 11.2H3.20002L0.400024 14ZM5.35252 8.575L7.40002 7.3325L9.44752 8.575L8.90502 6.2475L10.725 4.6725L8.32752 4.48L7.40002 2.275L6.47252 4.48L4.07502 4.6725L5.89502 6.2475L5.35252 8.575Z"
                  />
                </svg>

                <span
                  v-if="data.product.reviews_data.total_reviews !== 0"
                  class="text-sm text-gray-600"
                >
                  {{ __('Rated by') }}
                  {{ data.product.reviews_data.total_reviews }}
                  {{
                    data.product.reviews_data.total_reviews > 1
                      ? __('people')
                      : __('person')
                  }}.</span
                >
                <span v-else class="text-sm text-gray-600">
                  {{ __('This product has no reviews') }}
                </span>
              </div>
            </div>

            <div v-if="data.product.locations.length > 0" class="space-y-6">
              <div
                class="flex w-full flex-col items-start justify-center gap-y-1"
              >
                <div class="flex w-full items-center justify-start gap-x-2">
                  <Icon
                    name="local_shipping"
                    type="filled"
                    class="text-primary"
                  ></Icon>
                  <span class="text-sm">
                    {{ __('Delivered only in : \n') }}
                  </span>
                </div>
                <div class="flex w-full flex-col items-start justify-center">
                  <span
                    class="text-sm"
                    v-for="location in data.product.locations"
                  >
                    <strong>{{ location.country }}: </strong>
                    {{
                      location.cities
                        ? location.cities.map((city) => city.name).join(', ')
                        : __('Everywhere')
                    }}</span
                  >
                </div>
              </div>
            </div>

            <div class="w-full">
              <span class="text-zinc-600">
                {{ data.product.description }}
              </span>
            </div>

            <div class="flex w-full flex-col gap-y-6 pt-3">
              <span class="text-2xl font-medium text-zinc-800">
                {{ __('Requirements') }}
              </span>

              <div class="flex flex-col gap-y-4">
                <!-- Points Section -->
                <div class="flex items-center gap-x-2">
                  <div
                    class="flex w-full items-center justify-between border-b border-gray-200 pb-4"
                  >
                    <span class="w-full"> {{ __('Points') }}</span>

                    <div class="flex w-full items-center justify-end gap-x-2">
                      <div
                        class="flex flex-row items-center justify-center gap-x-2"
                      >
                        <span class="text-2xl font-medium">{{
                          data.product.points
                        }}</span>

                        <Icon
                          name="jobful_points_variant"
                          size="text-7xl"
                        ></Icon>
                      </div>
                      <template v-if="data.is_logged_in">
                        <template
                          v-if="data.user_points >= data.product.points"
                        >
                          <Icon
                            name="check_box"
                            type="filled"
                            class="text-success"
                          ></Icon>
                        </template>

                        <template v-else>
                          <Icon
                            name="disabled_by_default"
                            type="filled"
                            class="text-error"
                          ></Icon>
                        </template>
                      </template>
                    </div>
                  </div>
                </div>

                <div
                  v-for="(requirement, index) in data.product.requirements"
                  :key="index"
                  class="flex items-center gap-x-2"
                >
                  <div
                    class="flex w-full items-center justify-between border-b border-gray-200 pb-4"
                  >
                    <span class="w-full"> {{ requirement.name }}</span>

                    <div class="flex w-full items-center justify-end gap-x-2">
                      <span class="text-zinc-600">{{
                        requirement.needed
                      }}</span>
                      <template v-if="data.is_logged_in">
                        <template
                          v-if="requirement.current >= requirement.needed"
                        >
                          <Icon
                            name="check_box"
                            type="filled"
                            class="text-success"
                          ></Icon>
                        </template>

                        <template v-else>
                          <Icon
                            name="disabled_by_default"
                            type="filled"
                            class="text-error"
                          ></Icon>
                        </template>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-2">
              <div class="flex w-full flex-col space-y-8">
                <div v-for="attribute in data.product.attributes">
                  <div
                    v-if="attribute.attribute_name === 'Color'"
                    class="flex flex-col gap-y-4"
                  >
                    <span class="text-lg font-medium"> {{ __('Color') }}</span>

                    <div
                      class="flex w-full flex-row items-center justify-start gap-x-2 pl-1"
                    >
                      <div
                        v-for="value in attribute.values"
                        @click="
                          isAvailable(value) ? handleAttributeSelect(value) : ''
                        "
                        class="relative h-9 w-9 rounded-full shadow-md transition-all duration-200 ease-in-out hover:scale-105"
                        :style="{ backgroundColor: value.value }"
                        :class="[
                          selectedAttributes.includes(value)
                            ? 'ring-1 ring-secondary ring-offset-2'
                            : '',
                          isAvailable(value)
                            ? 'cursor-pointer'
                            : 'cursor-not-allowed opacity-50'
                        ]"
                      >
                        <!-- <div
                        v-if="!isAvailable(value)"
                        class="absolute left-1/2 top-1/2 h-0.5 w-4/5 -translate-x-1/2 -translate-y-1/2 rotate-45 transform bg-zinc-800"
                      ></div> -->
                      </div>
                    </div>
                  </div>
                  <div v-else class="flex flex-col gap-y-4">
                    <span class="text-lg font-medium">
                      {{ attribute.attribute_name }}</span
                    >

                    <div class="inline-flex flex-wrap gap-2">
                      <button
                        v-for="(value, valueIdx) in attribute.values"
                        :key="value.id"
                        :data-pill-group="attribute.attribute_name"
                        class="h-11 rounded-sm border bg-white px-4 py-2 text-center text-sm font-medium shadow-sm transition-all duration-200 ease-in-out hover:scale-105 hover:bg-gray-50"
                        :style="{ width: pillWidths[attribute.attribute_name] }"
                        @click="
                          isAvailable(value) ? handleAttributeSelect(value) : ''
                        "
                        :class="[
                          selectedAttributes.includes(value)
                            ? 'border-2 border-secondary'
                            : 'border-zinc-200',
                          isAvailable(value)
                            ? 'cursor-pointer'
                            : 'cursor-not-allowed opacity-50'
                        ]"
                      >
                        <span>{{ value.value }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->

            <div
              class="mt-auto flex h-full flex-col items-end justify-end gap-8"
            >
              <div
                v-if="data.is_logged_in"
                class="flex w-full items-end justify-end"
              >
                <div class="flex flex-col gap-y-2">
                  <span class="text-lg font-medium"> {{ __('Quantity') }}</span>

                  <div class="flex h-11 items-center justify-center">
                    <div
                      @click="quantity > 1 ? (quantity -= 1) : ''"
                      class="flex h-full items-center justify-center rounded-l-lg border border-zinc-200 p-2 hover:bg-gray-50"
                      :class="{
                        'cursor-not-allowed opacity-50': quantity <= 1,
                        'cursor-pointer': quantity > 1
                      }"
                    >
                      <Icon type="filled" name="remove"></Icon>
                    </div>

                    <div
                      class="flex h-full items-center justify-center border border-zinc-200 px-4 py-2"
                    >
                      <transition
                        enter-active-class="transition-all duration-300 ease-out"
                        enter-from-class="translate-y-2 opacity-0"
                        enter-to-class="translate-y-0 opacity-100"
                        leave-active-class="transition-all duration-200 ease-in"
                        leave-from-class="translate-y-0 opacity-100"
                        leave-to-class="-translate-y-2 opacity-0"
                        mode="out-in"
                      >
                        <span class="text-zinc-800" :key="quantity">
                          {{ quantity }}</span
                        >
                      </transition>
                    </div>

                    <div
                      @click="quantity += 1"
                      class="flex h-full cursor-pointer items-center justify-center rounded-r-lg border border-zinc-200 p-2 hover:bg-gray-50"
                    >
                      <Icon type="filled" name="add"></Icon>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="data.is_logged_in"
                class="grid w-full grid-cols-2 grid-rows-2 items-center justify-center gap-4 gap-x-6 pb-3 sm:flex sm:grid-rows-none sm:items-end sm:justify-end"
              >
                <button
                  @click="handleToggleWishList(data.product.id)"
                  class="btn btn-ghost hover-lift-no-shadow flex items-center justify-center gap-x-2"
                >
                  <template v-if="data.product.is_in_wishlist">
                    <Icon type="filled" name="favorite"></Icon>
                    <span class="text-primary">
                      {{ __('Remove from wishlist') }}
                    </span>
                  </template>
                  <template v-else>
                    <Icon name="favorite" type="outlined"></Icon>
                    <span class="text-primary">
                      {{ __('Add to wishlist') }}
                    </span>
                  </template>
                </button>

                <button
                  v-if="data.product.can_review"
                  class="btn btn-outline hover-lift-no-shadow flex items-center justify-start gap-x-2"
                  @click="isReviewModalOpen = true"
                >
                  <Icon
                    name="reviews"
                    type="filled"
                    class="text-primary"
                  ></Icon>
                  <span class="text-primary">
                    {{ __('Review this product') }}</span
                  >
                </button>

                <button
                  v-if="data.product.already_reviewed"
                  class="btn hover-lift-no-shadow flex cursor-default items-center justify-start gap-x-2 bg-success px-4 py-2"
                >
                  <Icon name="reviews" type="filled" class="text-white"></Icon>
                  <span class="text-white"> {{ __('Product rated') }}</span>
                </button>
                <div
                  class="flex items-center justify-center sm:justify-start"
                  :class="
                    data.product.can_review || data.product.already_reviewed
                      ? 'col-span-2'
                      : ''
                  "
                >
                  <button
                    @click="handleAddToCart"
                    class="btn btn-fill hover-lift-no-shadow disabled:cursor-not-allowed disabled:opacity-50"
                    :disabled="
                      !data.product.is_available_in_your_location ||
                      !data.product.meets_requirements ||
                      hasAvailableStock === false
                    "
                  >
                    <span class="text-white"> {{ __('Add to cart') }}</span>
                  </button>
                </div>
              </div>
            </div>

            <template v-if="!data.is_logged_in">
              <div
                class="flex h-full w-full items-center justify-center gap-x-6 pb-3 sm:justify-end"
              >
                <span class="text-sm text-zinc-600">
                  {{
                    __(
                      'In order to be able to purchase this product, you need to'
                    )
                  }}
                  <span
                    @click="redirectToPage('login.user')"
                    class="cursor-pointer text-primary underline"
                  >
                    {{ __('log in') }}
                  </span>
                  {{ __('or') }}
                  <span
                    @click="redirectToPage('front.auth.register')"
                    class="cursor-pointer text-primary underline"
                  >
                    {{ __('sign up') }}
                  </span>
                  .
                </span>
              </div>
            </template>

            <template v-if="data.is_logged_in">
              <div
                class="flex h-full w-full items-center justify-center gap-x-6 pb-3 sm:justify-end"
                v-if="!data.product.is_available_in_your_location"
              >
                <span class="text-sm text-zinc-600">
                  {{ __('This product is unavailable in your location.') }}
                </span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <!-- Recommended Products Section -->
    <CandidateRecommendedProducts
      v-if="
        data.is_logged_in && data.product?.recommended_products?.data.length > 0
      "
      :data="data"
    >
    </CandidateRecommendedProducts>
  </div>
  <CandidateShopProductsReviewModal
    :data="data"
    :is-loading="isLoadingModal"
    @cancel="isReviewModalOpen = false"
    @save="handleReviewProduct"
    v-if="isReviewModalOpen"
  ></CandidateShopProductsReviewModal>
</template>

<script setup>
  import { ref, computed, nextTick, onMounted, watch } from 'vue';
  import { __, redirectToPage } from '@/helpers';
  import Icon from '@/components/global/Icon.vue';
  import InputToggle from '@/components/form/input-toggle/input-toggle.vue';
  import { useToast } from 'vue-toastification';
  import CandidateRecommendedProducts from './components/CandidateRecommendedProducts.vue';
  import CandidateShopProductsReviewModal from './components/CandidateShopProductsReviewModal.vue';

  const toast = useToast();

  const props = defineProps({
    data: {
      type: Object,
      required: true
    }
  });
  const data = ref(props.data);
  const isLoadingModal = ref(false);
  const pillWidths = ref({});

  onMounted(async () => {
    await nextTick(); // Wait for DOM updates

    const storedMessage = sessionStorage.getItem('toast_message');
    if (storedMessage) {
      const { type, message } = JSON.parse(storedMessage);
      toast[type](message);

      //
      sessionStorage.removeItem('toast_message');
    }
    await nextTick();
    data.value.product.attributes.forEach((attribute) => {
      const pills = document.querySelectorAll(
        `[data-pill-group="${attribute.attribute_name}"]`
      );
      let maxWidth = 0;

      pills.forEach((pill) => {
        pill.style.width = 'auto';
        maxWidth = Math.max(maxWidth, pill.offsetWidth);
        pill.style.width = '';
      });

      pillWidths.value[attribute.attribute_name] = `${maxWidth}px`;
    });
  });

  const quantity = ref(1);

  // Sample product data - in real app, this would come from API

  const isNotMobile = computed(() => {
    return window.innerWidth >= 640;
  });

  const selectedImage = ref(
    data.value.product.images.find((item) => item.is_primary)?.path
  );
  function makePrimary(targetImage) {
    selectedImage.value = targetImage.path;
  }
  const isReviewModalOpen = ref(false);
  function changeImage(direction) {
    if (direction === 'next') {
      if (
        data.value.product.images.findIndex(
          (item) => item.path === selectedImage.value
        ) ===
        data.value.product.images.length - 1
      )
        return;

      selectedImage.value =
        data.value.product.images[
          data.value.product.images.findIndex(
            (item) => item.path === selectedImage.value
          ) + 1
        ].path;
    } else {
      if (
        data.value.product.images.findIndex(
          (item) => item.path === selectedImage.value
        ) === 0
      )
        return;

      selectedImage.value =
        data.value.product.images[
          data.value.product.images.findIndex(
            (item) => item.path === selectedImage.value
          ) - 1
        ].path;
    }
  }

  const variants_combinations = ref(
    data.value.product.variants.map((variant) => {
      let combination = variant.attributes
        .map((attr) => `${attr.category}:${attr.label}`)
        .sort()
        .join('|');

      return {
        variant_id: variant.id,
        stock: variant.stock,
        updated_at: variant.updated_at,

        combination
      };
    })
  );

  const selectedAttributes = ref([]);

  const handleAttributeSelect = (value) => {
    let attributeTypeAlreadyExists = selectedAttributes.value.some(
      (attribute) => attribute.attribute_name === value.attribute_name
    );

    if (selectedAttributes.value.includes(value)) {
      selectedAttributes.value = selectedAttributes.value.filter(
        (current_value) => current_value !== value
      );
    } else if (attributeTypeAlreadyExists) {
      selectedAttributes.value = selectedAttributes.value.filter(
        (current_value) => current_value.attribute_name !== value.attribute_name
      );
      selectedAttributes.value.push(value);
    } else {
      selectedAttributes.value.push(value);
    }
  };

  const isAvailable = (value) => {
    let test_selectedAttributes = selectedAttributes.value;

    if (test_selectedAttributes.find((attr) => attr.value === value.label)) {
      return true; // Already selected
    }

    let attributeExists = test_selectedAttributes.some(
      (attr) => attr.attribute_name === value.attribute_name
    );

    if (attributeExists) {
      test_selectedAttributes = test_selectedAttributes.filter(
        (attr) => attr.attribute_name !== value.attribute_name
      );

      test_selectedAttributes.push(value);
    }

    let combination = test_selectedAttributes
      .map((attr) => `${attr.attribute_name}:${attr.label}`)
      .sort()
      .join('|');

    // if the value added is not replacing another one we know we are going to have more attributes then before so we add |
    if (combination.length > 0 && !attributeExists)
      combination = combination + '|';

    // if the attribute did not replace an old one construct the new combination
    if (!attributeExists)
      combination = combination + value.attribute_name + ':' + value.label;

    // if no attributes are selected, check if there is atleast one variant with stock > 0 for the current value
    // if there are selected attributes and the current type of attribute already exists,
    // still check if there is atleast one variant with stock > 0 for the current value

    if (selectedAttributes.value.length === 0 || attributeExists) {
      const hasExistingVariantAndInStock = variants_combinations.value.find(
        (variant) =>
          variant.combination.includes(
            `${value.attribute_name}:${value.label}`
          ) && variant.stock > 0
      );
      if (hasExistingVariantAndInStock) {
        return true;
      } else {
        return false;
      }
    }

    // if the combination has atleast 1 attribute previously selected, but if we add this new one and is not yet finished, let all be true

    if (
      combination.split('|').length - 1 <
      data.value.product.attributes.length - 1
    ) {
      return true;
    }

    // re do it so we have the sort the same
    combination = combination.split('|').sort().join('|');
    const variant = variants_combinations.value.find(
      (variant) => variant.combination === combination
    );

    if (variant && variant.stock > 0) {
      return true;
    } else {
      return false;
    }
  };

  const getCurrentVariantSelected = () => {
    let combination = selectedAttributes.value
      .map((attr) => `${attr.attribute_name}:${attr.label}`)
      .sort()
      .join('|');

    const variant = variants_combinations.value.find(
      (variant) => variant.combination === combination
    );

    return variant;
  };

  const handleAddToCart = async () => {
    if (!data.value.product.is_available_in_your_location) {
      toast.error(__('This product is unavailable in your location.'));
      return;
    }
    const variant = getCurrentVariantSelected();
    try {
      if (canPurchase(variant)) {
        const response = await axios.post(
          route(
            'redesign.candidate.shop.products.add_to_cart',
            variant.variant_id
          ),
          {
            quantity: quantity.value,
            product_updated_at: data.value.product.updated_at,
            product_variant_updated_at: variant.updated_at
          }
        );
        if (response.data.message) {
          data.value.product.max_quantity_per_user_remaining -= quantity.value;

          sessionStorage.setItem(
            'toast_message',
            JSON.stringify({
              type: 'success',
              message: response.data.message
            })
          );

          window.location.reload();
        } else {
          toast.error(__('Failed to add product to cart.'));
        }
      }
    } catch (error) {
      if (error.response && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(__('An error occurred while adding to cart.'));
      }
    }
  };

  const handleToggleWishList = async (productId) => {
    const response = await axios.post(
      route('redesign.candidate.shop.products.toggle_wishlist', productId)
    );

    if (response.data.message) {
      toast.success(response.data.message);
      data.value.product.is_in_wishlist = !data.value.product.is_in_wishlist;
    } else {
      toast.error(response.data.message);
    }
  };

  const isAvailableToPurchase = computed(() => {
    if (data.value.product.points > data.value.user_points) {
      return false;
    }
    if (quantity.value > data.value.product.max_quantity_per_user_remaining) {
      return false;
    }
    return data.value.product.requirements.every(
      (requirement) => requirement.current >= requirement.needed
    );
  });

  const canPurchase = (variant) => {
    if (!variant) {
      toast.error(__('Please select a valid variant.'));
      return false;
    }
    if (variant.stock <= 0) {
      toast.error(__('This variant is out of stock.'));
      return false;
    }
    if (quantity.value <= 0) {
      toast.error(__('Please select a valid quantity.'));
      return false;
    }

    if (data.value.product.points * quantity.value > data.value.user_points) {
      toast.error(
        __('You do not have enough points to purchase this product.')
      );
      return false;
    }
    if (
      !data.value.product.requirements.every(
        (requirement) => requirement.current >= requirement.needed
      )
    ) {
      toast.error(
        __('You do not meet the requirements to purchase this product.')
      );
      return false;
    }
    if (variant.stock < quantity.value) {
      toast.error(
        __(
          'Insufficient stock for the selected variant. Available: [ {stock} ] ',
          {
            stock: variant.stock
          }
        )
      );
      return false;
    }

    if (
      data.value.product.max_quantity_per_user &&
      quantity.value > data.value.product.max_quantity_per_user_remaining
    ) {
      if (data.value.product.in_cart) {
        if (data.value.product.max_quantity_per_user_remaining === 0) {
          toast.error(
            __(
              `This product has a limit of [ ${data.value.product.max_quantity_per_user} ] per user. You have already purchased [ ${data.value.product.quantity_already_bought_by_user} ] and have [ ${data.value.product.in_cart} ] in your cart, so you cannot buy any more.`
            )
          );
          return false;
        } else {
          toast.error(
            __(
              `This product has a limit of [ ${data.value.product.max_quantity_per_user} ] per user. You have already purchased [ ${data.value.product.quantity_already_bought_by_user} ] and have [ ${data.value.product.in_cart} ] in your cart, so you can only buy [ ${data.value.product.max_quantity_per_user_remaining} ] more.`
            )
          );
          return false;
        }
      } else {
        toast.error(
          __(
            `This product has a limit of [ ${data.value.product.max_quantity_per_user} ] per user. You have already purchased [ ${data.value.product.quantity_already_bought_by_user} ], so you can only buy [ ${data.value.product.max_quantity_per_user_remaining} ] more.`
          )
        );
        return false;
      }
    }

    return true;
  };

  const handleReviewProduct = async (eventData) => {
    isLoadingModal.value = true;
    try {
      const response = await axios.post(
        route('redesign.candidate.shop.products.review', data.value.product.id),
        {
          rating: eventData.rating
        }
      );

      if (response.data.message) {
        toast.success(response.data.message);
        data.value.product.can_review = false;
        isReviewModalOpen.value = false;
        data.value.product.reviews_data.total_reviews += 1;
        data.value.product.already_reviewed = true;
        // Recalculate average rating
        if (data.value.product.reviews_data.average_rating === null) {
          data.value.product.reviews_data.average_rating = eventData.rating;
        } else {
          data.value.product.reviews_data.average_rating = (
            (data.value.product.reviews_data.average_rating *
              (data.value.product.reviews_data.total_reviews - 1) +
              eventData.rating) /
            data.value.product.reviews_data.total_reviews
          ).toFixed(1);
        }
      } else {
        toast.error(__('Failed to submit review.'));
      }
    } catch (error) {
      console.error('Error reviewing product:', error);
      if (error.response && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(__('An error occurred while submitting your review.'));
      }
    } finally {
      isLoadingModal.value = false;
    }
  };

  const hasAvailableStock = computed(() => {
    return data.value.product.variants.some((variant) => variant.stock > 0);
  });
</script>
